import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import { Target, BookOpen, FileText, Calendar, Trophy, Flame, TrendingUp } from 'lucide-react';
import { Progress } from '../components/ui/progress';

const ProgressPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const headers = getAuthHeader();
      const response = await api.getProgressStats(headers);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary-600"></div>
      </div>
    );
  }

  const topicsProgress = stats.total_topics > 0 ? (stats.completed_topics / stats.total_topics) * 100 : 0;
  const homeworkProgress = stats.total_homework > 0 ? (stats.completed_homework / stats.total_homework) * 100 : 0;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-heading font-bold text-slate-900 mb-2">Fortschritt</h1>
        <p className="text-lg text-slate-600">Verfolge deine Lernfortschritte</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl p-8 text-white shadow-lg" data-testid="level-card">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
              <Trophy size={32} />
            </div>
            <div>
              <p className="text-sm opacity-90">Dein Level</p>
              <p className="text-4xl font-heading font-bold">{stats.level}</p>
            </div>
          </div>
          <p className="text-sm opacity-90">Weiter so! Sammle mehr Punkte, um aufzusteigen.</p>
        </div>

        <div className="bg-gradient-to-br from-orange-400 to-red-500 rounded-3xl p-8 text-white shadow-lg" data-testid="streak-card">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
              <Flame size={32} />
            </div>
            <div>
              <p className="text-sm opacity-90">Lernstreak</p>
              <p className="text-4xl font-heading font-bold">{stats.streak}</p>
            </div>
          </div>
          <p className="text-sm opacity-90">Lerne jeden Tag, um deine Streak zu halten!</p>
        </div>

        <div className="bg-gradient-to-br from-teal-400 to-emerald-500 rounded-3xl p-8 text-white shadow-lg" data-testid="points-card">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
              <Target size={32} />
            </div>
            <div>
              <p className="text-sm opacity-90">Punkte</p>
              <p className="text-4xl font-heading font-bold">{stats.points}</p>
            </div>
          </div>
          <p className="text-sm opacity-90">Jede Aufgabe bringt dich weiter!</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-indigo-100 rounded-xl">
              <BookOpen className="text-indigo-600" size={24} />
            </div>
            <h2 className="text-2xl font-heading font-bold text-slate-900">Themen</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-slate-600">Abgeschlossene Themen</p>
              <p className="text-2xl font-heading font-bold text-slate-900">{stats.completed_topics}/{stats.total_topics}</p>
            </div>
            <Progress value={topicsProgress} className="h-3" />
            <p className="text-sm text-slate-500">
              {Math.round(topicsProgress)}% aller Themen abgeschlossen
            </p>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-teal-100 rounded-xl">
              <FileText className="text-teal-600" size={24} />
            </div>
            <h2 className="text-2xl font-heading font-bold text-slate-900">Hausaufgaben</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-slate-600">Erledigte Hausaufgaben</p>
              <p className="text-2xl font-heading font-bold text-slate-900">{stats.completed_homework}/{stats.total_homework}</p>
            </div>
            <Progress value={homeworkProgress} className="h-3" />
            <p className="text-sm text-slate-500">
              {Math.round(homeworkProgress)}% aller Hausaufgaben erledigt
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
        <h2 className="text-2xl font-heading font-bold text-slate-900 mb-6">Übersicht</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-6 bg-indigo-50 rounded-2xl">
            <BookOpen className="mx-auto text-indigo-600 mb-3" size={32} />
            <p className="text-3xl font-heading font-bold text-slate-900 mb-1">{stats.total_subjects}</p>
            <p className="text-sm text-slate-600">Fächer</p>
          </div>
          <div className="text-center p-6 bg-teal-50 rounded-2xl">
            <FileText className="mx-auto text-teal-600 mb-3" size={32} />
            <p className="text-3xl font-heading font-bold text-slate-900 mb-1">{stats.total_homework}</p>
            <p className="text-sm text-slate-600">Hausaufgaben</p>
          </div>
          <div className="text-center p-6 bg-orange-50 rounded-2xl">
            <Calendar className="mx-auto text-orange-600 mb-3" size={32} />
            <p className="text-3xl font-heading font-bold text-slate-900 mb-1">{stats.upcoming_exams}</p>
            <p className="text-sm text-slate-600">Anst. Klausuren</p>
          </div>
          <div className="text-center p-6 bg-purple-50 rounded-2xl">
            <TrendingUp className="mx-auto text-purple-600 mb-3" size={32} />
            <p className="text-3xl font-heading font-bold text-slate-900 mb-1">{stats.level}</p>
            <p className="text-sm text-slate-600">Level</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressPage;
